// ============================================
// InvoiceDetailDrawer.jsx
// File: src/components/invoices/InvoiceDetailDrawer.jsx
// 
// Slide-out drawer showing invoice details and actions
// Consistent with CustomerDetailDrawer and JobDetailDrawer
// ============================================

import React, { useState, useEffect } from 'react';
import { 
  X, 
  FileText, 
  Mail, 
  Send, 
  RefreshCw,
  DollarSign, 
  CreditCard,
  Link, 
  CheckCheck,
  Download,
  Edit3,
  Trash2,
  User,
  MapPin,
  Calendar,
  Clock,
  AlertCircle,
  Check,
  Users,
  ExternalLink,
  Loader,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { Smartphone } from 'lucide-react';
import api, { invoicesApi } from '../../services/api';

const InvoiceDetailDrawer = ({ 
  invoice, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete, 
  onRecordPayment,
  onRefresh,
  stripeConnectEnabled = false,
  businessSettings = {}
}) => {
  const [loading, setLoading] = useState(false);
  const [fullInvoice, setFullInvoice] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [smsNote, setSmsNote] = useState(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(true);
  const [showJobsBreakdown, setShowJobsBreakdown] = useState(true);
  const [error, setError] = useState(null);

  // Load full invoice details when drawer opens
  useEffect(() => {
    if (isOpen && invoice?.id) {
      setFullInvoice(null); // don't flash the previously opened invoice
      setSmsNote(null);
      loadInvoiceDetails();
    }
  }, [isOpen, invoice?.id]);

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await invoicesApi.getById(invoice.id);
      setFullInvoice(response.data);
    } catch (err) {
      console.error('Error loading invoice details:', err);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  // Use full invoice data if loaded, otherwise use passed invoice
  const data = fullInvoice || invoice;
  if (!data) return null;

  const customerName = data.customerName || data.customer?.name || data.job?.customer?.name || 'Unknown';
  const customerEmail = data.customerEmail || data.customer?.email || data.job?.customer?.email;
  const customerPhone = data.customer?.phone || data.job?.customer?.phone;
  const canTakePayment = data.status !== 'Paid' && data.status !== 'Void' && data.status !== 'Cancelled';
  const hasPayments = data.payments && data.payments.length > 0;

  // Handle actions
  const handleSendEmail = async () => {
    if (!customerEmail) return;
    
    try {
      setSendingEmail(true);
      // Use the existing endpoint (sends HTML email, no PDF attachment)
      await invoicesApi.send(data.id);
      await loadInvoiceDetails();
      onRefresh?.();
    } catch (err) {
      console.error('Error sending invoice:', err);
      setError('Failed to send invoice email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendSms = async () => {
    if (!customerPhone) return;

    try {
      setSendingSms(true);
      setSmsNote(null);
      await invoicesApi.sendSms(data.id);
      setSmsNote({ ok: true, text: `Invoice texted to ${customerName}` });
    } catch (err) {
      console.error('Error texting invoice:', err);
      setSmsNote({ ok: false, text: err.response?.data?.message || 'Failed to text invoice' });
    } finally {
      setSendingSms(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get(`/invoices/${data.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${data.invoiceNumber || `INV-${data.id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    try {
      const res = await api.get(`/pay/link/${data.id}`);
      const paymentLink = res.data.url;

      try {
        await navigator.clipboard.writeText(paymentLink);
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = paymentLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Error getting payment link:', err);
      setError('Could not get the payment link');
    }
  };

  const formatEmailSentTime = (date) => {
    if (!date) return '';
    const sentDate = new Date(date);
    const now = new Date();
    const diffMs = now - sentDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div className={`fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <FileText className="text-primary-600" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {data.invoiceNumber || `INV-${data.id}`}
                </h2>
                <p className="text-sm text-gray-600">{customerName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          {/* Status and Amount */}
          <div className="flex items-center justify-between mt-4">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${getStatusColor(data.status)}`}>
              {data.status}
            </span>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.totalAmount)}</p>
              {data.amountDue > 0 && data.amountDue < data.totalAmount && (
                <p className="text-sm text-orange-600 font-medium">
                  {formatCurrency(data.amountDue)} due
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 min-h-0 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-primary-600" size={32} />
            </div>
          ) : error ? (
            <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
              <button 
                onClick={loadInvoiceDetails}
                className="text-red-600 text-sm font-medium mt-2 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Invoice Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-gray-600">Issued:</span>
                  <span className="font-medium">{formatDate(data.invoiceDate)}</span>
                </div>

                {data.dueDate && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className={data.status === 'Overdue' ? 'text-red-500' : 'text-gray-400'} />
                    <span className="text-gray-600">Due:</span>
                    <span className={`font-medium ${data.status === 'Overdue' ? 'text-red-600' : ''}`}>
                      {formatDate(data.dueDate)}
                    </span>
                  </div>
                )}

                {/* Consolidated Invoice Badge */}
                {data.isConsolidated && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users size={16} className="text-purple-500" />
                    <span className="text-purple-700 font-medium">
                      Consolidated Invoice • {data.jobCount || 0} jobs
                    </span>
                  </div>
                )}

                {/* Billing Period */}
                {data.isConsolidated && data.billingPeriodStart && data.billingPeriodEnd && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-sm text-purple-700">
                      <span className="font-medium">Billing Period:</span>{' '}
                      {formatDate(data.billingPeriodStart)} - {formatDate(data.billingPeriodEnd)}
                    </p>
                  </div>
                )}

                {/* Email Status */}
                {data.emailSent && (
                  <div className="flex items-center gap-2 text-sm bg-green-50 border border-green-200 rounded-lg p-3">
                    <Check size={16} className="text-green-600" />
                    <span className="text-green-700">
                      Email sent {formatEmailSentTime(data.emailSentAt)}
                      {data.emailSendCount > 1 && ` (${data.emailSendCount} times)`}
                    </span>
                  </div>
                )}

                {/* No Email Warning */}
                {!customerEmail && (
                  <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertCircle size={16} className="text-amber-600" />
                    <span className="text-amber-700">Customer has no email on file</span>
                  </div>
                )}
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <User size={16} />
                  Customer
                </h3>
                <p className="font-medium text-gray-900">{customerName}</p>
                {customerEmail && (
                  <p className="text-sm text-gray-600">{customerEmail}</p>
                )}
                {data.job?.address && !data.isConsolidated && (
                  <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                    <MapPin size={14} />
                    {data.job.address}
                  </p>
                )}
              </div>

              {/* Line Items / Jobs Breakdown */}
              {data.isConsolidated && data.consolidatedJobs?.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowJobsBreakdown(!showJobsBreakdown)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="text-sm font-semibold text-gray-700">
                      Jobs ({data.consolidatedJobs.length})
                    </h3>
                    {showJobsBreakdown ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  
                  {showJobsBreakdown && (
                    <div className="divide-y divide-gray-100">
                      {data.consolidatedJobs.map((job, idx) => (
                        <div key={idx} className="p-3 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{job.address}</p>
                              <p className="text-xs text-gray-500">
                                {job.completedDate ? formatDate(job.completedDate) : 'N/A'}
                              </p>
                              {job.services?.map((s, sIdx) => (
                                <span key={sIdx} className="text-xs text-gray-400">
                                  {s.name}{sIdx < job.services.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatCurrency(job.finalAmount || job.quoteAmount || 0)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Single Job Services */}
              {!data.isConsolidated && data.job?.jobServices?.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-700">Services</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {data.job.jobServices.map((js, idx) => (
                      <div key={idx} className="p-3 flex justify-between items-center">
                        <span className="text-sm text-gray-900">
                          {js.serviceType?.name || 'Service'}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(js.totalPrice)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(data.subTotal)}</span>
                </div>
                {data.taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">{formatCurrency(data.taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-bold text-gray-900">{formatCurrency(data.totalAmount)}</span>
                </div>
                {data.amountPaid > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Paid</span>
                    <span className="font-medium">-{formatCurrency(data.amountPaid)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base pt-2 border-t border-gray-300">
                  <span className="font-bold text-primary-700">Amount Due</span>
                  <span className="font-bold text-primary-700">{formatCurrency(data.amountDue)}</span>
                </div>
              </div>

              {/* Payment History */}
              {hasPayments && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                    className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <h3 className="text-sm font-semibold text-green-700 flex items-center gap-2">
                      <DollarSign size={16} />
                      Payment History ({data.payments.length})
                    </h3>
                    {showPaymentHistory ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  
                  {showPaymentHistory && (
                    <div className="divide-y divide-gray-100">
                      {data.payments.map((payment, idx) => (
                        <div key={idx} className="p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {formatCurrency(payment.amount)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {payment.paymentMethod} • {formatDate(payment.paymentDate)}
                              </p>
                              {payment.transactionId && (
                                <p className="text-xs text-gray-400">Ref: {payment.transactionId}</p>
                              )}
                            </div>
                            <Check size={16} className="text-green-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {data.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-amber-700 mb-2">Notes</h3>
                  <p className="text-sm text-amber-900">{data.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed Actions Footer */}
        <div className="shrink-0 bg-white border-t border-gray-200 p-4 space-y-3">
          {/* Primary Actions */}
          <div className="grid grid-cols-2 gap-3">
            {canTakePayment && (
              <button
                onClick={() => onRecordPayment?.(data)}
                className="btn btn-success flex items-center justify-center gap-2"
              >
                <DollarSign size={18} />
                Record Payment
              </button>
            )}
            
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="btn btn-secondary flex items-center justify-center gap-2"
            >
              {downloadingPdf ? (
                <Loader size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Download PDF
            </button>
          </div>

          {/* Text Invoice */}
          {customerPhone && (
            <div className="space-y-2">
              <button
                onClick={handleSendSms}
                disabled={sendingSms}
                className="w-full btn bg-green-600 hover:bg-green-700 text-white text-sm py-2 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sendingSms ? <Loader size={14} className="animate-spin" /> : <Smartphone size={14} />}
                {sendingSms ? 'Sending...' : 'Text Invoice'}
              </button>
              {smsNote && (
                <p className={`text-xs ${smsNote.ok ? 'text-green-700' : 'text-red-600'}`}>{smsNote.text}</p>
              )}
            </div>
          )}

          {/* Secondary Actions */}
          <div className="flex flex-wrap gap-2">
            {customerEmail && (
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className={`flex-1 btn text-sm py-2 flex items-center justify-center gap-1 ${
                  data.emailSent 
                    ? 'btn-secondary border-blue-300 text-blue-700' 
                    : 'btn-primary'
                }`}
              >
                {sendingEmail ? (
                  <Loader size={14} className="animate-spin" />
                ) : data.emailSent ? (
                  <RefreshCw size={14} />
                ) : (
                  <Send size={14} />
                )}
                {sendingEmail ? 'Sending...' : data.emailSent ? 'Resend Email' : 'Send Email'}
              </button>
            )}

            {stripeConnectEnabled && canTakePayment && (
              <button
                onClick={handleCopyPaymentLink}
                className={`flex-1 btn text-sm py-2 flex items-center justify-center gap-1 ${
                  copiedLink
                    ? 'bg-green-100 text-green-700 border-green-300'
                    : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                }`}
              >
                {copiedLink ? <CheckCheck size={14} /> : <Link size={14} />}
                {copiedLink ? 'Copied!' : 'Payment Link'}
              </button>
            )}

            <button
              onClick={() => onEdit?.(data)}
              className="btn btn-secondary text-sm py-2 px-3"
            >
              <Edit3 size={14} />
            </button>

            <button
              onClick={() => onDelete?.(data.id)}
              className="btn text-sm py-2 px-3 text-red-600 hover:bg-red-50 border border-red-200"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceDetailDrawer;